import type { Vec2, OBB, AABB } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';

function normalizeVec(v: Vec2): Vec2 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1]);
  if (len < 1e-10) return [1, 0];
  return [v[0] / len, v[1] / len];
}

/** The lot rectangle behind a front edge, reaching `depth` away from its source. */
export function obbFromFrontEdge(
  frontA: Vec2,
  frontB: Vec2,
  depth: number,
  towardSource: Vec2
): OBB {
  const frontVec: Vec2 = [frontB[0] - frontA[0], frontB[1] - frontA[1]];
  const frontLen = Math.sqrt(frontVec[0] * frontVec[0] + frontVec[1] * frontVec[1]);
  const frontMid: Vec2 = [(frontA[0] + frontB[0]) / 2, (frontA[1] + frontB[1]) / 2];
  const center: Vec2 = [
    frontMid[0] - towardSource[0] * (depth / 2),
    frontMid[1] - towardSource[1] * (depth / 2),
  ];
  return { center, axisU: normalizeVec(frontVec), halfU: frontLen / 2, halfV: depth / 2 };
}

export function obbToAABB(obb: OBB): AABB {
  const corners = obbCorners(obb);
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const c of corners) {
    if (c[0] < minX) minX = c[0];
    if (c[1] < minY) minY = c[1];
    if (c[0] > maxX) maxX = c[0];
    if (c[1] > maxY) maxY = c[1];
  }
  return { minX, minY, maxX, maxY };
}

/* Circles grow by far more than rounding, so boxes they keep apart never touch. */
const REACH_PAD = 1e-6;

/**
 * Whether two boxes may meet: false only when the circles round them lie apart, which a separating
 * axis test would also find, at a fraction of its cost.
 */
export function obbsMayMeet(a: OBB, b: OBB): boolean {
  const dx = a.center[0] - b.center[0];
  const dy = a.center[1] - b.center[1];
  const reach = Math.hypot(a.halfU, a.halfV) + Math.hypot(b.halfU, b.halfV) + REACH_PAD;
  return dx * dx + dy * dy <= reach * reach;
}
